import React, {useEffect, useState} from "react";
import axios from "axios";
import {Box, Flex, Icon, Text} from "@chakra-ui/react";
import {LuStickyNote} from "react-icons/lu";
import {url} from "../crudRequests";
import {APP_LOCALE} from "./Dependent/format";

// Notes a clinician chose to show you.
//
// Not a section of the chart and not behind a permission: there is no grade that
// unlocks clinical writing, and there must never be one. Each of these is a
// single note somebody at the clinic read, chose, and published — to you, or to
// a guardian, one at a time. That is why it appears on a home screen rather than
// behind a tab: it is a message about somebody, not a part of their record that
// opened up.
//
// Read-only, with nothing to press. You cannot reply to it, edit it or make it
// disappear; what the clinic wrote is the clinic's. Withdrawing it is done
// there, and it goes immediately.
//
// One renderer for both screens. The guardian's copy arrives inside the
// dependent overview — composed server-side, because a composition is where
// harmless fragments add up to a disclosure — and the account holder's own is
// fetched here. Two renderers would eventually describe the same note in two
// different ways.

export function SharedNotesList({notes, heading}) {
  if (!notes || notes.length === 0) return null;

  return (
    <Box mt={6}>
      <Flex align="center" gap={2} mb={2}>
        <Icon as={LuStickyNote} color="gray.400" />
        <Text fontSize="11px" letterSpacing="0.04em" textTransform="uppercase" color="gray.500">
          {heading || "From your clinic"}
        </Text>
      </Flex>
      {notes.map((note) => (
        <Box
          key={note.noteId}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="10px"
          p={4}
          bg="white"
          mb={3}
        >
          {note.title && (
            <Text fontWeight="600" color="#1a1f36" mb={1}>
              {note.title}
            </Text>
          )}
          <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
            {note.body}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={2}>
            {formatWrittenAt(note.writtenAt)}
          </Text>
        </Box>
      ))}
    </Box>
  );
}

/**
 * The account holder's own shared notes.
 *
 * Renders nothing at all when there are none, and nothing when the request
 * fails: a home screen must not grow an error box because an optional block
 * could not load. The clinic's own screens report their failures.
 */
export default function SharedNotes({state, heading}) {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!state?.jwt) return;
    let cancelled = false;
    axios
      .get(`${url}/patient-app/notes`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + state.jwt,
          "X-Tenant": "patient",
        },
        withCredentials: true,
      })
      .then((response) => !cancelled && setNotes(response.data?.items || []))
      .catch(() => !cancelled && setNotes([]));
    return () => {
      cancelled = true;
    };
  }, [state?.jwt]);

  return <SharedNotesList notes={notes} heading={heading} />;
}

function formatWrittenAt(ms) {
  if (!ms && ms !== 0) return "";
  const value = new Date(Number(ms));
  if (Number.isNaN(value.getTime())) return "";
  return value.toLocaleDateString(APP_LOCALE, {dateStyle: "medium"});
}
